/**
 * Generic, domain-agnostic export helpers.
 *
 * Centralises the CSV escaping + Blob-download boilerplate that was previously
 * duplicated across domains (audit trail, user logs) and adds Excel (.xlsx)
 * generation via the already-installed `xlsx` package.
 *
 * Rows are represented as arrays of primitives aligned to a `headers` array —
 * callers map their domain objects to `(string | number | null)[]` rows and
 * supply the human-readable column headers.
 */

import * as XLSX from 'xlsx';

export type ExportCell = string | number | null | undefined;
export type ExportRow = ExportCell[];

/** Excel needs a UTF-8 BOM prepended so it opens CSVs with the correct encoding. */
const UTF8_BOM = '﻿';

/** Quote a CSV field when it contains a comma, quote, or newline; escape quotes by doubling. */
function escapeCsvField(value: ExportCell): string {
  if (value == null) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string from headers + rows. Includes a UTF-8 BOM so Excel opens
 * it with the right encoding. Rows are emitted in the order given.
 */
export function toCsv(headers: string[], rows: ExportRow[]): string {
  const headerLine = headers.map(escapeCsvField).join(',');
  const body = rows.map((row) => row.map(escapeCsvField).join(','));
  return UTF8_BOM + [headerLine, ...body].join('\r\n');
}

/**
 * Build an .xlsx workbook (single sheet) from headers + rows and return it as a
 * binary array suitable for a Blob with the spreadsheet MIME type.
 */
export function toXlsx(headers: string[], rows: ExportRow[], sheetName = 'Sheet1'): Uint8Array {
  const aoa: ExportCell[][] = [headers, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  const workbook = XLSX.utils.book_new();
  // Sheet names are capped at 31 chars by the spec; trim to stay valid.
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
}

/**
 * Trigger a browser download for the given content. Accepts a string (CSV/JSON)
 * or binary (xlsx) payload. Works entirely client-side — no backend round-trip.
 */
export function downloadBlob(
  content: string | Uint8Array | Blob,
  filename: string,
  mimeType: string,
): void {
  // A copied Uint8Array is backed by a plain ArrayBuffer, which satisfies
  // BlobPart — the source Uint8Array can be typed over the wider ArrayBufferLike
  // (incl. SharedArrayBuffer), which the DOM's BlobPart type rejects.
  // A Blob (e.g. a server-generated file) is used as-is so its bytes and type
  // are preserved rather than being re-wrapped.
  const blob =
    content instanceof Blob
      ? content
      : new Blob([typeof content === 'string' ? content : new Uint8Array(content)], {
          type: mimeType,
        });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const EXPORT_MIME = {
  csv: 'text/csv;charset=utf-8;',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  json: 'application/json',
} as const;

/** `YYYY-MM-DD` stamp for export filenames (local date). */
export function exportDateStamp(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
